import { Input, Select } from '@/components/ui/FormControls';
import { useState } from 'react';
import Link from '@/components/common/AccessLink';
import ExportActions from '@/components/common/ExportActions';
import { usePurchases, usePurchasePeople } from '@/hooks/usePurchases';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useAuth } from '@/contexts/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DatePicker from '@/components/common/DatePicker';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import PurchaseLifecycleActions from '@/components/purchases/PurchaseLifecycleActions';
import PurchaseBulkDelete from '@/components/purchases/PurchaseBulkDelete';
import { purchaseInputClass as input } from '@/components/purchases/PurchasePaymentFields';
import {
  purchaseStatusLabel,
  purchaseLifecycleLabel,
  purchasePaymentState,
  purchaseBalance,
  saleMoney,
} from '@klothiq/domain';

export default function PurchaseRecordsList({ bin = false }: { bin?: boolean }) {
  const { can } = useAuth();
  const [search, setSearch] = useState(''),
    [from, setFrom] = useState(''),
    [to, setTo] = useState(''),
    [status, setStatus] = useState('all'),
    [supplier, setSupplier] = useState(''),
    [lifecycle, setLifecycle] = useState<'active' | 'archived' | 'deleted'>(
      bin ? 'deleted' : 'active',
    ),
    [page, setPage] = useState(0);
  const [payment, setPayment] = useState('all'),
    [mode, setMode] = useState('all'),
    [creator, setCreator] = useState(''),
    [selected, setSelected] = useState<Set<string>>(new Set());
  const people = usePurchasePeople(true);
  const suppliers = useSuppliers();
  const query = usePurchases({ search, from, to, status, supplier, lifecycle, payment, mode });
  const rows = (query.data || []).filter((p) => !creator || p.created_by === creator);
  const chosen = rows.filter((p) => selected.has(p.id));
  const toggle = (id: string) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const pages = Math.max(1, Math.ceil(rows.length / 25));
  const visible = rows.slice(Math.min(page, pages - 1) * 25, (Math.min(page, pages - 1) + 1) * 25);
  return (
    <div className="space-y-5">
      <PageHeader
        title={bin ? 'Deleted purchases' : 'Purchases'}
        description={
          bin
            ? 'Restore purchase records. Permanent deletion is not available.'
            : 'Supplier purchase records · optional item details and account debit'
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {!bin && can('purchases.create') && (
              <Link
                to="/purchases/new"
                className="min-h-11 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
              >
                + Add purchase
              </Link>
            )}
            <ExportActions
              disabled={!rows.length || query.isFetching || query.isError}
              excelOptions={() => ({
                fileName: 'Purchases',
                sheetName: 'Purchases',
                filtersSummary: `${from || 'All dates'} – ${to || 'Today'} · ${lifecycle} · ${search}`,
                columns: [
                  { header: 'Purchase', key: 'purchase_number' },
                  { header: 'Supplier', key: 'supplier' },
                  { header: 'Date', key: 'purchase_date', format: 'date' },
                  { header: 'Amount', key: 'total_amount', format: 'currency' },
                  { header: 'Recorded payments', key: 'paid_amount', format: 'currency' },
                  { header: 'Supplier balance', key: 'supplier_balance' },
                  { header: 'Delivery', key: 'delivery' },
                  { header: 'Lifecycle', key: 'lifecycle' },
                  { header: 'Payment', key: 'payment' },
                  { header: 'Created by', key: 'creator' },
                ],
                data: rows.map((p) => ({
                  ...p,
                  supplier: p.suppliers?.name || '—',
                  supplier_balance:
                    purchaseBalance(p) === null ? 'Not tracked' : purchaseBalance(p),
                  delivery: purchaseStatusLabel(p),
                  lifecycle: purchaseLifecycleLabel(p) || 'Active',
                  payment: purchasePaymentState(p),
                  creator: people.data?.find((x) => x.id === p.created_by)?.full_name || 'Unknown',
                })),
              })}
              exportType="purchases"
              data={rows.map((p) => ({
                ...p,
                supplier_name: p.suppliers?.name || 'Supplier',
                balance_due: purchaseBalance(p),
                payment_status: purchasePaymentState(p),
              }))}
            />
          </div>
        }
      />
      <section
        aria-label="Purchase filters"
        className="grid gap-3 rounded-2xl border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        <label className="space-y-1 text-sm">
          Search
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Reference or supplier"
            className={input}
            helpLabel="Search"
          />
        </label>
        <label className="space-y-1 text-sm">
          Supplier
          <Select
            value={supplier}
            onChange={(e) => {
              setSupplier(e.target.value);
              setPage(0);
            }}
            className={input}
            helpLabel="Supplier"
          >
            <option value="">All suppliers</option>
            {suppliers.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </label>
        <label className="space-y-1 text-sm">
          Delivery status
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
            className={input}
            helpLabel="Delivery status"
          >
            <option value="all">All statuses</option>
            <option value="ordered">Pending</option>
            <option value="received">Delivered</option>
            <option value="draft">Draft</option>
            <option value="partially_received">Legacy partial receipt</option>
          </Select>
        </label>
        <label className="space-y-1 text-sm">
          From
          <DatePicker
            value={from}
            onChange={(v) => {
              setFrom(v || '');
              setPage(0);
            }}
            helpLabel="From"
          />
        </label>
        <label className="space-y-1 text-sm">
          To
          <DatePicker
            value={to}
            min={from}
            onChange={(v) => {
              setTo(v || '');
              setPage(0);
            }}
            helpLabel="To"
          />
        </label>
        {!bin && (
          <label className="space-y-1 text-sm">
            Show
            <Select
              value={lifecycle}
              onChange={(e) => {
                setLifecycle(e.target.value as typeof lifecycle);
                setPage(0);
              }}
              className={input}
              helpLabel="Show"
            >
              <option value="active">Active purchases</option>
              <option value="archived">Archived purchases</option>
              <option value="deleted">Recycle bin</option>
            </Select>
          </label>
        )}
        <label className="space-y-1 text-sm">
          Payment
          <Select
            className={input}
            value={payment}
            onChange={(e) => {
              setPayment(e.target.value);
              setPage(0);
            }}
            helpLabel="Payment"
          >
            {[
              ['all', 'All payments'],
              ['not_recorded', 'Not recorded'],
              ['unpaid', 'Unpaid'],
              ['partially_paid', 'Partially paid'],
              ['paid', 'Paid'],
            ].map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        </label>
        <label className="space-y-1 text-sm">
          Entry type
          <Select
            className={input}
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setPage(0);
            }}
            helpLabel="Entry type"
          >
            {[
              ['all', 'All entry types'],
              ['amount_only', 'Amount only'],
              ['itemized', 'Itemized record'],
              ['catalog', 'Legacy catalog'],
              ['direct', 'Legacy direct'],
            ].map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        </label>
        <label className="space-y-1 text-sm">
          Created by
          <Select
            className={input}
            value={creator}
            onChange={(e) => {
              setCreator(e.target.value);
              setPage(0);
            }}
            helpLabel="Created by"
          >
            <option value="">All users</option>
            {people.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name}
              </option>
            ))}
          </Select>
        </label>
      </section>
      {lifecycle !== 'deleted' && can('purchases.delete') && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <Input
              type="checkbox"
              className="h-5 w-5 accent-primary"
              checked={!!visible.length && visible.every((p) => selected.has(p.id))}
              onChange={(e) =>
                setSelected((previous) => {
                  const next = new Set(previous);
                  visible.forEach((p) => {
                    if (e.target.checked) next.add(p.id);
                    else next.delete(p.id);
                  });
                  return next;
                })
              }
              helpLabel="Select page"
            />
            Select page
          </label>
          <PurchaseBulkDelete
            disabled={query.isFetching || query.isError}
            rows={chosen}
            label={`Delete selected (${chosen.length})`}
          />
          <PurchaseBulkDelete
            disabled={query.isFetching || query.isError}
            rows={rows}
            label="Delete all eligible in this filter"
          />
        </div>
      )}
      {query.isLoading ? (
        <LoadingState />
      ) : query.error ? (
        <ErrorState message={query.error.message} onRetry={query.refetch} />
      ) : !rows.length ? (
        <p className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">
          No purchases match these filters.
        </p>
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-2xl border border-border bg-card lg:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/30 text-xs text-muted-foreground">
                <tr>
                  {[
                    'Purchase / supplier',
                    'Date',
                    'Amount',
                    'Delivery',
                    'Payment',
                    'Balance',
                    'Actions',
                  ].map((k) => (
                    <th key={k} className="p-3">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {can('purchases.delete') && lifecycle !== 'deleted' && (
                          <Input
                            aria-label={`Select ${p.purchase_number}`}
                            type="checkbox"
                            className="h-5 w-5 accent-primary"
                            checked={selected.has(p.id)}
                            onChange={() => toggle(p.id)}
                          />
                        )}
                        <Link to={`/purchases/${p.id}`} className="font-semibold text-primary">
                          {p.purchase_number}
                        </Link>
                      </div>
                      <p>{p.suppliers?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.workflow_mode === 'record_only'
                          ? 'Purchase record'
                          : 'Legacy stock order'}{' '}
                        {purchaseLifecycleLabel(p) && ` · ${purchaseLifecycleLabel(p)}`}
                      </p>
                    </td>
                    <td className="p-3">
                      {p.purchase_date}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {people.data?.find((x) => x.id === p.created_by)?.full_name ||
                          'Unknown creator'}
                      </p>
                    </td>
                    <td className="p-3 font-semibold">{saleMoney(p.total_amount)}</td>
                    <td className="p-3 capitalize">{purchaseStatusLabel(p)}</td>
                    <td className="p-3 capitalize">
                      {purchasePaymentState(p).replaceAll('_', ' ')}
                    </td>
                    <td className="p-3">
                      {purchaseBalance(p) === null ? 'Not tracked' : saleMoney(purchaseBalance(p)!)}
                    </td>
                    <td className="p-3">
                      <PurchaseLifecycleActions purchase={p} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-3 lg:hidden">
            {visible.map((p) => (
              <article key={p.id} className="glass-card space-y-3 p-4">
                {can('purchases.delete') && lifecycle !== 'deleted' && (
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <Input
                      type="checkbox"
                      className="h-5 w-5 accent-primary"
                      checked={selected.has(p.id)}
                      onChange={() => toggle(p.id)}
                      helpLabel="Select"
                    />
                    Select {p.purchase_number}
                  </label>
                )}
                <div className="flex justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/purchases/${p.id}`} className="font-semibold text-primary">
                      {p.purchase_number}
                    </Link>
                    <h2 className="break-words text-sm">{p.suppliers?.name}</h2>
                    <p className="text-xs text-muted-foreground">
                      {p.purchase_date} ·{' '}
                      {people.data?.find((x) => x.id === p.created_by)?.full_name ||
                        'Unknown creator'}
                    </p>
                  </div>
                  <strong className="shrink-0">{saleMoney(p.total_amount)}</strong>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {purchaseLifecycleLabel(p) && (
                    <span className="rounded-full bg-amber-500/10 px-3 py-1">
                      {purchaseLifecycleLabel(p)}
                    </span>
                  )}
                  <span className="rounded-full bg-primary/10 px-3 py-1 capitalize">
                    {purchaseStatusLabel(p)}
                  </span>
                  <span className="rounded-full border border-border px-3 py-1 capitalize">
                    {purchasePaymentState(p).replaceAll('_', ' ')}
                  </span>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link className="min-h-11 py-3 text-sm text-primary" to={`/purchases/${p.id}`}>
                    View details
                  </Link>
                  <PurchaseLifecycleActions purchase={p} />
                </div>
              </article>
            ))}
          </div>
          <nav
            aria-label="Purchase pages"
            className="flex items-center justify-between gap-3 text-sm"
          >
            <button
              disabled={page <= 0}
              className="min-h-11 rounded-xl border border-border px-4 disabled:opacity-40"
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <span>
              Page {Math.min(page, pages - 1) + 1} / {pages} · {rows.length} records
            </span>
            <button
              disabled={page >= pages - 1}
              className="min-h-11 rounded-xl border border-border px-4 disabled:opacity-40"
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </nav>
        </>
      )}
    </div>
  );
}
